import React, { useEffect, useRef, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { Button, Select, Textarea } from "@windmill/react-ui";
import OfflineServices from "services/OfflineServices";
import { notifySuccess, notifyError } from "utils/toast";
import { OFFLINE_STATUS } from "components/languageDriver/OfflineTable";

// Offline query (ticket) shown as a conversation, in the same right-side
// panel design as the chat drawer:
//   • header: the agent's contact details (name, email, phone)
//   • agent's query on the left, the employee's resolution on the right
//   • assigned employee: type the resolution → Send (saved, still assigned)
//     → Resolve (closes the ticket; the agent then sees the answer)
//   • Admin: assign / re-assign to an employee, or reject with a reason
const OfflineQueryDrawer = ({ query, isAdmin, employees, currentUserId, onClose, onChanged }) => {
  const [ticket, setTicket] = useState(query);
  const [text, setText] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("");
  const [showAdmin, setShowAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const isOpen = Boolean(query);

  useEffect(() => {
    setTicket(query);
    // Pre-fill with whatever answer is already saved, so "changing" it means
    // editing the existing text instead of retyping it from scratch.
    setText(query?.resolution || "");
    setReason("");
    setEmployeeId(query?.assigned_to ? String(query.assigned_to) : "");
    // An unassigned query needs the Admin's decision first.
    setShowAdmin(Boolean(query) && query.status === "open");
  }, [query]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [ticket?.resolution, ticket?.status]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const status = OFFLINE_STATUS[ticket?.status] || OFFLINE_STATUS.open;
  const isMine = ticket && String(ticket.assigned_to) === String(currentUserId);
  const canAnswer = ticket?.status === "assigned" && (isAdmin || isMine);
  const canAdminAct = isAdmin && (ticket?.status === "open" || ticket?.status === "assigned");
  const savedAnswer = (ticket?.resolution || "").trim();

  // Run an action, then reload this ticket and the list behind it.
  const run = async (action, { closeAfter = false } = {}) => {
    setBusy(true);
    try {
      const res = await action();
      if (!res?.status) {
        notifyError(res?.message || "Something went wrong.");
        return false;
      }
      notifySuccess(res.message || "Done.");
      onChanged && onChanged();
      if (closeAfter) {
        onClose();
      } else {
        const fresh = await OfflineServices.getOfflineQuery(ticket.id).catch(() => null);
        if (fresh?.status) setTicket(fresh.data);
      }
      return true;
    } catch (err) {
      notifyError(err?.response?.data?.message || "Something went wrong.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const sendAnswer = async () => {
    const value = text.trim();
    if (!value) return;
    if (await run(() => OfflineServices.replyOfflineQuery(ticket.id, value))) setText("");
  };

  // Resolve with what's typed (if anything), otherwise with the saved answer.
  const resolve = () =>
    run(() => OfflineServices.resolveOfflineQuery(ticket.id, text.trim()), { closeAfter: false }).then(
      (ok) => ok && setText("")
    );

  const bubble = ({ side, name, body, time, note }) => (
    <div className={`p-1 my-1 flex flex-col ${side === "left" ? "items-start" : "items-end"}`}>
      <div className={`text-xs mb-1 ${side === "left" ? "text-gray-400" : "text-green-600"}`}>{name}</div>
      <div
        className={`rounded-lg p-2 break-words text-sm whitespace-pre-wrap ${
          side === "left" ? "bg-gray-300 text-black" : "bg-green-500 text-white"
        }`}
        style={{ width: "fit-content", maxWidth: "80%" }}
      >
        {body}
      </div>
      {(time || note) && (
        <div className="text-xs text-gray-400 mt-0.5">{[time, note].filter(Boolean).join(" · ")}</div>
      )}
    </div>
  );

  return (
    <>
      {/* Dimmed backdrop — inline colour: Tailwind 1.9 has no bg-opacity-30. */}
      <div
        className={`fixed inset-0 z-40 transition-colors duration-200 ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{ backgroundColor: isOpen ? "rgba(0, 0, 0, 0.25)" : "rgba(0, 0, 0, 0)" }}
        onClick={onClose}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full bg-white dark:bg-gray-800 shadow-xl flex flex-col transform transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ maxWidth: "620px" }}
        aria-hidden={!isOpen}
      >
        {ticket && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                  {ticket.company_name || "Agent"}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}>
                    {status.label}
                  </span>
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Ticket {ticket.ref_no || `#${ticket.id}`} · {ticket.created_at}
                  {ticket.assigned_name ? ` · Assigned to ${ticket.assigned_name}` : ""}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-red-600 focus:outline-none text-2xl leading-none"
                aria-label="Close"
              >
                <IoIosClose />
              </button>
            </div>

            {/* Agent contact details */}
            <div className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 space-y-1">
              <div>
                <span className="font-medium">Name: </span>
                {ticket.agent_name || "-"}
              </div>
              <div>
                <span className="font-medium">Email: </span>
                {ticket.agent_email ? (
                  <a className="text-green-700 underline" href={`mailto:${ticket.agent_email}`}>
                    {ticket.agent_email}
                  </a>
                ) : (
                  "-"
                )}
              </div>
              <div>
                <span className="font-medium">Phone: </span>
                {ticket.agent_mobile ? (
                  <a className="text-green-700 underline" href={`tel:${ticket.agent_mobile}`}>
                    {ticket.agent_mobile}
                  </a>
                ) : (
                  "-"
                )}
              </div>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto px-3 py-2 bg-gray-50 dark:bg-gray-900">
              {bubble({
                side: "left",
                name: ticket.agent_name || ticket.company_name || "Agent",
                body: (
                  <>
                    {ticket.subject && <div className="font-semibold mb-1">{ticket.subject}</div>}
                    {ticket.content}
                  </>
                ),
                time: ticket.created_at,
              })}

              {savedAnswer &&
                bubble({
                  side: "right",
                  name: isMine ? "You" : ticket.assigned_name || "Employee",
                  body: savedAnswer,
                  time: ticket.status === "resolved" ? ticket.resolved_at : null,
                  note: ticket.status === "resolved" ? "Resolved" : "Not resolved yet — the agent can't see this",
                })}

              {ticket.status === "rejected" && (
                <p className="text-center text-xs text-red-600 my-3">
                  Rejected{ticket.rejected_at ? ` on ${ticket.rejected_at}` : ""}: {ticket.rejected_reason}
                </p>
              )}
              {ticket.status === "open" && (
                <p className="text-center text-xs text-gray-500 my-3">Not assigned to an employee yet.</p>
              )}
              {ticket.status === "assigned" && !canAnswer && !savedAnswer && (
                <p className="text-center text-xs text-gray-500 my-3">
                  Waiting for {ticket.assigned_name || "the employee"} to answer.
                </p>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Assigned employee: type the resolution, Send, then Resolve */}
            {canAnswer && (
              <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700">
                <Textarea
                  rows="3"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={savedAnswer ? "Change the resolution…" : "Type the resolution for this query…"}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-500">
                    {savedAnswer ? "Press Resolve to close the ticket." : "Send the resolution, then Resolve."}
                  </span>
                  <div className="flex gap-2">
                    <Button layout="outline" disabled={busy || !text.trim()} onClick={sendAnswer}>
                      Send
                    </Button>
                    <Button disabled={busy || (!text.trim() && !savedAnswer)} onClick={resolve}>
                      Resolve
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Admin: assign / re-assign, or reject */}
            {canAdminAct && (
              <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <button
                  className="text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none"
                  onClick={() => setShowAdmin((v) => !v)}
                >
                  {showAdmin ? "▾" : "▸"} Admin: {ticket.status === "assigned" ? "re-assign" : "assign"} or reject
                </button>
                {showAdmin && (
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      <Select className="h-10 text-sm" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                        <option value="">Select an employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </Select>
                      <Button
                        disabled={busy || !employeeId || String(employeeId) === String(ticket.assigned_to)}
                        onClick={() => run(() => OfflineServices.assignOfflineQuery(ticket.id, Number(employeeId)))}
                      >
                        Assign
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        rows="1"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason for rejecting (the agent will see it)"
                      />
                      <Button
                        layout="outline"
                        disabled={busy || !reason.trim()}
                        onClick={() => run(() => OfflineServices.rejectOfflineQuery(ticket.id, reason.trim()))}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
};

export default OfflineQueryDrawer;
